import * as idb from 'idb-keyval';
import { type AtomEffect, DefaultValue } from 'recoil';

/**
 * A token that can be returned from a serializer to skip serialization.
 */
export const persistRecoilSkip = Symbol('persistRecoilSkip');

type Serializer<T, SerializedT> = {
    /**
     * Serialize the value for storage, or return `persistRecoilSkip` to skip serialization.
     */
    serialize?: (value: T) => SerializedT | typeof persistRecoilSkip;
    deserialize?: (value: SerializedT) => T;
};

/**
 * Recoil effect to persist the value in the IndexedDB store.
 * Note: This effect can suspend rendering while reading the value from the storage.
 */
export function persistRecoilEffect<T, SerializedT = T>(
    options: {
        /**
         * Serialize the value for storage.
         */
        serializer?: Serializer<T, SerializedT>;

        /**
         * Whether to initialize the value asynchronously.
         * This option should be set to true when the atom is used with `useRecoilCallback`.
         * The main reason is because, recoil doesn't support `set` calls `in `useRecoilCallback` when the state is pending
         */
        initAsync?: boolean;

        /**
         * When `initAsync` is set to true, this function is used to test if a state can be overidden by the storage.
         */
        canOverride?: (prev: T) => boolean;
    } = {}
): AtomEffect<T> {
    const { serializer, initAsync = false, canOverride = () => true } = options;

    const serialize = serializer?.serialize || ((value) => value as unknown as SerializedT);
    const deserialize = serializer?.deserialize || ((value) => value as unknown as T);

    return ({ node, trigger, setSelf, onSet }) => {
        const key = `@recoil/${node.key}`;
        if (trigger === 'get') {
            const promise = idb
                .get<SerializedT>(key)
                .then((value) => (value !== undefined ? deserialize(value) : undefined));

            if (initAsync) {
                promise.then((result) => {
                    if (result !== undefined) {
                        setSelf((prev) => {
                            if (prev instanceof DefaultValue || canOverride(prev)) {
                                return result;
                            }

                            // Session has been overidden, we ignore the storage
                            return prev;
                        });
                    }
                });
            } else {
                setSelf(
                    promise.then((result) => {
                        if (result !== undefined) {
                            return result;
                        }
                        return new DefaultValue();
                    })
                );
            }
        }

        onSet((newValue) => {
            if (newValue instanceof DefaultValue) {
                return idb.del(key);
            }
            const serialized = serialize(newValue);
            if (serialized === persistRecoilSkip) {
                return;
            }
            return idb.set(key, serialized);
        });
    };
}
